import {
  ChangeEvent,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Box, FormGroup } from "@material-ui/core";
import { debounce, isEqual, map, startCase } from "lodash";
import { usePostHog } from "posthog-js/react";

import { CheckboxField } from "components";
import { UiParam, UiParams } from "hooks/useQueryParams/types";
import { longDebounceTime } from "services/constants";
import { EXCHANGE_FILTER_SORT } from "services/postHog/constants";
import theme from "theme";

import RangeTextInput from "./RangeTextInput";

export type RangeParam = {
  gte: UiParam;
  lte: UiParam;
};
export type RangeOptions = Record<string, RangeParam>;

interface RangeFilterControlProps {
  rangeKey: string;
  rangeOptions: RangeOptions;
  uiParams: UiParams;
  setUiParams: (setState: (uiParams: UiParams) => UiParams) => void;
  title: ReactNode;
  startAdornment?: ReactNode;
  inputComponent?: FC;
}

const RangeFilterControl: FC<RangeFilterControlProps> = ({
  rangeKey,
  rangeOptions,
  uiParams,
  setUiParams,
  title,
  startAdornment,
  inputComponent,
}) => {
  const posthog = usePostHog();
  const gteKey = `${rangeKey}.gte`;
  const lteKey = `${rangeKey}.lte`;
  const isPresetRangeSelected = useCallback(
    (name: string) => {
      const matchesGte = isEqual(
        uiParams[gteKey].value,
        rangeOptions[name].gte.value,
      );
      const matchesLte = isEqual(
        uiParams[lteKey].value,
        rangeOptions[name].lte.value,
      );

      return matchesGte && matchesLte;
    },
    [gteKey, lteKey, uiParams, rangeOptions],
  );
  const isAnyPresetRangeChecked = useMemo(
    () =>
      Object.keys(rangeOptions)
        .map(name => isPresetRangeSelected(name))
        .some(element => element),
    [rangeOptions, isPresetRangeSelected],
  );
  const [displayMin, setDisplayMin] = useState(
    isAnyPresetRangeChecked ? "" : uiParams[gteKey].value[0] ?? "",
  );
  const [displayMax, setDisplayMax] = useState(
    isAnyPresetRangeChecked ? "" : uiParams[lteKey].value[0] ?? "",
  );

  const trackRangePostHogFilterEvent = useCallback(() => {
    const filterBy =
      rangeKey === "converted_list_price"
        ? "List Price"
        : rangeKey === "converted_deal_amount_or_list_price"
        ? "List Price/Sale Price"
        : rangeKey.length < 4
        ? rangeKey.toUpperCase()
        : startCase(rangeKey.replace("_", " "));

    posthog?.capture(EXCHANGE_FILTER_SORT, {
      actionType: "range",
      filterBy,
    });
  }, [posthog, rangeKey]);

  const handleSelectPresetRange = useCallback(
    (checked: boolean, name: string) => {
      if (checked) {
        trackRangePostHogFilterEvent();
        setUiParams(prev => ({
          ...prev,
          page: { ...prev.page, value: "1" },
          [gteKey]: {
            ...prev[gteKey],
            value: rangeOptions[name].gte.value,
          },
          [lteKey]: {
            ...prev[lteKey],
            value: rangeOptions[name].lte.value,
          },
        }));
      } else {
        setUiParams(prev => ({
          ...prev,
          page: { ...prev.page, value: "1" },
          [gteKey]: {
            ...prev[gteKey],
            value: [],
          },
          [lteKey]: {
            ...prev[lteKey],
            value: [],
          },
        }));
      }
      setDisplayMin("");
      setDisplayMax("");
    },
    [trackRangePostHogFilterEvent, gteKey, lteKey, setUiParams, rangeOptions],
  );

  const [isChangeCommitted, setIsChangeCommitted] = useState(true);
  const handleUpdateUiParam = useMemo(
    () => (updated: string, key: string) => {
      if (isAnyPresetRangeChecked) {
        trackRangePostHogFilterEvent();
        setUiParams(prev => {
          const updatedUiParams = {
            ...prev,
            page: { ...prev.page, value: "1" },
            [key]: {
              ...prev[key],
              value: !!updated ? [updated] : [],
            },
          };

          const updatedGteValue = updatedUiParams[gteKey].value[0];
          const updatedLteValue = updatedUiParams[lteKey].value[0];
          const isRangeDefined = !!updatedGteValue && !!updatedLteValue;
          const isRangeInvalid =
            Number(updatedGteValue) > Number(updatedLteValue);

          if (isRangeDefined && isRangeInvalid) {
            // the range defined is invalid, fix it for the user
            const validRangeUiParams = {
              ...prev,
              page: { ...prev.page, value: "1" },
              [gteKey]: {
                ...prev[gteKey],
                value: [updatedLteValue],
              },
              [lteKey]: {
                ...prev[lteKey],
                value: [updatedGteValue],
              },
            };
            setDisplayMin(updatedLteValue);
            setDisplayMax(updatedGteValue);
            return validRangeUiParams;
          }

          if (key.includes("gte")) {
            setDisplayMin(updated);
          } else {
            setDisplayMax(updated);
          }
          return updatedUiParams;
        });
      } else {
        setUiParams(prev => {
          const updatedUiParams = {
            ...prev,
            page: { ...prev.page, value: "1" },
            [key]: {
              ...prev[key],
              value: !!updated ? [updated] : [],
            },
          };

          if (
            updatedUiParams[gteKey].value.length ||
            updatedUiParams[lteKey].value.length
          ) {
            trackRangePostHogFilterEvent();
          }

          const updatedGteValue = updatedUiParams[gteKey].value[0];
          const updatedLteValue = updatedUiParams[lteKey].value[0];
          const isRangeDefined = !!updatedGteValue && !!updatedLteValue;

          const isRangeInvalid =
            Number(updatedGteValue) > Number(updatedLteValue);
          if (isRangeDefined && isRangeInvalid) {
            // the range defined is invalid, fix it for the user
            const validRangeUiParams = {
              ...prev,
              [gteKey]: {
                ...prev[gteKey],
                value: [updatedLteValue],
              },
              [lteKey]: {
                ...prev[lteKey],
                value: [updatedGteValue],
              },
            };
            setDisplayMin(updatedLteValue);
            setDisplayMax(updatedGteValue);
            return validRangeUiParams;
          }
          return updatedUiParams;
        });
      }
      setIsChangeCommitted(true);
    },
    [
      trackRangePostHogFilterEvent,
      gteKey,
      lteKey,
      isAnyPresetRangeChecked,
      setUiParams,
    ],
  );
  const debouncedHandleUpdateUiParam = useMemo(
    () =>
      debounce(
        (updated, key) => handleUpdateUiParam(updated, key),
        longDebounceTime,
      ),
    [handleUpdateUiParam],
  );

  const [hasSetUrlParamsToRange, setHasSetUrlParamsToRange] = useState(false);
  useEffect(() => {
    if (!isAnyPresetRangeChecked && !hasSetUrlParamsToRange) {
      if (uiParams[gteKey].value.length && !displayMin) {
        setUiParams(prev => ({
          page: { ...prev.page, value: "1" },
          ...prev,
          [gteKey]: {
            ...prev[gteKey],
          },
        }));
        setDisplayMin(uiParams[gteKey].value[0]);
      }
      if (uiParams[lteKey].value.length && !displayMax) {
        setUiParams(prev => ({
          page: { ...prev.page, value: "1" },
          ...prev,
          [lteKey]: {
            ...prev[lteKey],
          },
        }));
        setDisplayMax(uiParams[lteKey].value[0]);
      }
    }
    setHasSetUrlParamsToRange(true);
  }, [
    gteKey,
    lteKey,
    displayMin,
    displayMax,
    isAnyPresetRangeChecked,
    hasSetUrlParamsToRange,
    uiParams,
    setUiParams,
  ]);

  // this useEffect watches for a change to uiParams & syncs display state
  useEffect(() => {
    if (isChangeCommitted) {
      if (uiParams[gteKey].value[0] !== displayMin.replace(/,/g, "")) {
        setDisplayMin(
          isAnyPresetRangeChecked ? "" : uiParams[gteKey].value[0] ?? "",
        );
      }
      if (uiParams[lteKey].value[0] !== displayMax.replace(/,/g, "")) {
        setDisplayMax(
          isAnyPresetRangeChecked ? "" : uiParams[lteKey].value[0] ?? "",
        );
      }
    }
  }, [
    gteKey,
    lteKey,
    uiParams,
    isChangeCommitted,
    displayMin,
    displayMax,
    isAnyPresetRangeChecked,
  ]);

  return (
    <>
      {title}
      <FormGroup>
        {map(rangeOptions, (_option, key) => (
          <CheckboxField
            dense
            key={key}
            checked={isPresetRangeSelected(key)}
            onChange={(_, checked) => handleSelectPresetRange(checked, key)}
            label={key}
            checkboxColor={theme.custom.palette.purple.main}
          />
        ))}
        <Box display="flex">
          <RangeTextInput
            rangeKey={rangeKey}
            label="Min"
            value={displayMin}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
              const updated = event.target.value;
              setDisplayMin(updated);
              setIsChangeCommitted(false);
              debouncedHandleUpdateUiParam(updated.replace(/,/g, ""), gteKey);
            }}
            startAdornment={startAdornment}
            inputComponent={inputComponent}
          />
          <RangeTextInput
            rangeKey={rangeKey}
            label="Max"
            value={displayMax}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
              const updated = event.target.value;
              setDisplayMax(updated);
              setIsChangeCommitted(false);
              debouncedHandleUpdateUiParam(updated.replace(/,/g, ""), lteKey);
            }}
            startAdornment={startAdornment}
            inputComponent={inputComponent}
          />
        </Box>
      </FormGroup>
    </>
  );
};

export default RangeFilterControl;
